<template>
    <BaseDialog v-model:open="isOpen" :title="t('chat.managerReview.title', { name: revieweeName || t('chat.managerReview.fallbackName') })">
        <div v-if="draft" class="flex flex-col gap-5">
            <div class="flex flex-col gap-1">
                <p class="body-small text-ink-muted">Drafted from your conversation. Review or edit each comment, then submit it to Workday.</p>
            </div>

            <template v-if="!submitted">
                <div v-if="draft.loaded_from_workday" class="flex flex-col gap-2 rounded-md bg-surface-sunken p-3" data-testid="manager-review-loaded-from-workday">
                    <p class="body-small text-ink-muted">Comments already in your Workday review are shown as they are. Empty ones are drafted from this conversation.</p>
                    <div>
                        <BaseButton theme="secondary" data-testid="redraft-manager-review" :disabled="loading || redrafting" @click="emit('redraft')">
                            Redraft from conversation
                        </BaseButton>
                    </div>
                    <div v-if="redraftError" class="body-small text-notification-error-text" role="alert" data-testid="redraft-manager-review-error">{{ redraftError }}</div>
                </div>

                <section v-for="section in localDraft.sections" :key="section.section_id" class="flex flex-col gap-2">
                    <h4 class="body-semibold text-ink">{{ section.label }}</h4>

                    <div class="flex flex-col gap-1">
                        <div class="body-small text-control-label">{{ section.label }} comment</div>
                        <BaseTextarea
                            v-model:input="section.comment"
                            rows="3"
                            :disabled="loading"
                            :aria-label="`${section.label} comment`"
                            :placeholder="`Add a comment for ${section.label}.`"
                            class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    </div>

                    <div v-if="sectionRatingScope(section)" class="flex flex-col gap-1" :data-testid="`manager-review-section-rating-${section.section_id}`">
                        <div class="body-small text-control-label">{{ section.label }} rating</div>
                        <BaseSelect
                            v-model="section.rating"
                            :options="ratingChoices(sectionRatingScope(section))"
                            :disabled="loading"
                            placeholder="Choose a rating"
                            :aria-label="`${section.label} rating`" />
                    </div>
                </section>

                <section class="flex flex-col gap-1">
                    <h4 class="body-semibold text-ink">Overall review comment</h4>
                    <BaseTextarea
                        v-model:input="localDraft.overall_comment"
                        rows="3"
                        :disabled="loading"
                        aria-label="Overall review comment"
                        placeholder="Add an overall comment for the review."
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                </section>

                <!-- Only where the tenant's own template says a manager may rate, and only with
                     the ids it supplied. Nadia never suggests a rating; the manager picks one. -->
                <section v-if="overallRatingScope" class="flex flex-col gap-2" data-testid="manager-review-overall-rating">
                    <h4 class="body-semibold text-ink">Overall rating</h4>
                    <BaseSelect v-model="localDraft.overall_rating" :options="overallRatingChoices" :disabled="loading" placeholder="Choose a rating" aria-label="Overall rating" />
                    <p v-if="overallRatingMissing" class="body-small text-ink-muted">Workday requires an overall rating on this review before it can be submitted.</p>
                </section>

                <p v-if="sectionRatingMissing" class="body-small text-ink-muted">Workday requires a rating on each rated section before this review can be submitted.</p>

                <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="manager-review-submitted">
                <i class="bi bi-check-circle text-2xl text-chip-status-done-text" aria-hidden="true"></i>
                <p class="nadia-chat text-ink">Submitted to {{ revieweeName || "your report" }}'s Workday review.</p>
                <BaseButton theme="primary" @click="isOpen = false">Done</BaseButton>
            </div>
        </div>
        <template v-if="draft && !submitted" #footer>
            <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">Cancel</BaseButton>
            <BaseButton theme="primary" data-testid="submit-manager-review-dialog" :disabled="submitDisabled" class="flex items-center justify-center gap-1" @click="handleSubmit">
                <LoadingSpinner v-if="loading" class="h-4" />Submit to Workday
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import { i18n } from "~vue/i18n";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref, watch } from "vue";

const { t } = i18n.global;

interface CardSection {
    section_id: string;
    label: string;
    comment: string;
    rating: string;
    rating_scope: string;
    workday_comment?: string;
    workday_rating?: string;
}
interface CardDraft {
    review_ref: string;
    overall_comment: string;
    overall_rating: string;
    workday_overall_comment?: string;
    sections: CardSection[];
    loaded_from_workday?: boolean;
}
interface RatingChoice {
    review_rating_id: string;
    label: string;
}
interface RatingChoiceOption {
    key: string;
    label: string;
}
/** One scope of the tenant's discovered template, as the server authored it (LAB-1264). */
export interface RatingScope {
    scope: string;
    label: string;
    can_rate: boolean;
    rating_required: boolean;
    input_kind: string;
    options: RatingChoice[];
}

interface Props {
    draft: CardDraft | null;
    revieweeName?: string | null;
    messageId: number;
    ratingOptions?: RatingScope[];
    redrafting?: boolean;
    redraftError?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ redraft: [] }>();
const isOpen = defineModel<boolean>("open", { default: false });

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
// Not `loading`: that one is deferred by 200ms for flicker, which is long enough to double-submit.
const inFlight = ref(false);
const noticeMessage = ref("");

function cloneDraft(source: CardDraft | null): CardDraft {
    if (!source) {
        return { review_ref: "", overall_comment: "", overall_rating: "", sections: [] };
    }
    const cloned = { overall_rating: "", ...JSON.parse(JSON.stringify(source)) };
    cloned.sections = cloned.sections.map((section: CardSection) => ({ ...section, rating: section.rating ?? "", rating_scope: section.rating_scope ?? "" }));
    return cloned;
}

const localDraft = reactive<CardDraft>(cloneDraft(props.draft));

// A scope the template does not let a manager rate simply has no control -- never an empty picker,
// which would let a manager believe they had rated when the submit would refuse it. A section's
// scope comes from the server (LAB-1538); a section it could not match to the template has none.
function rateableScope(scopeKey: string): RatingScope | null {
    if (!scopeKey) {
        return null;
    }
    return (props.ratingOptions ?? []).find((scope) => scope.scope === scopeKey && scope.can_rate && scope.input_kind === "reference" && scope.options.length > 0) ?? null;
}
function ratingChoices(scope: RatingScope | null): RatingChoiceOption[] {
    return (scope?.options ?? []).map((option) => ({ key: option.review_rating_id, label: option.label }));
}
function sectionRatingScope(section: CardSection): RatingScope | null {
    return rateableScope(section.rating_scope);
}

const overallRatingScope = computed<RatingScope | null>(() => rateableScope("overall"));
const overallRatingChoices = computed<RatingChoiceOption[]>(() => ratingChoices(overallRatingScope.value));
const overallRatingMissing = computed(() => Boolean(overallRatingScope.value?.rating_required) && !localDraft.overall_rating);
const sectionRatingMissing = computed(() => localDraft.sections.some((section) => Boolean(sectionRatingScope(section)?.rating_required) && !section.rating));
// A redraft replaces the card when it lands, so a submit racing it could be followed by fresh text.
const submitDisabled = computed(() => inFlight.value || loading.value || props.redrafting || overallRatingMissing.value || sectionRatingMissing.value);

// Reset the editable copy and state whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            Object.assign(localDraft, cloneDraft(draft));
            submitted.value = false;
            noticeMessage.value = "";
        }
    },
);

async function handleSubmit() {
    if (inFlight.value) {
        return;
    }
    inFlight.value = true;
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_manager_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await execute(ChatAction.SUBMIT_MANAGER_REVIEW_DRAFT, { message_id: props.messageId, draft: JSON.parse(JSON.stringify(localDraft)) }, { sync: true });
        if (result?.payload?.submitted) {
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? "We couldn't submit this review to Workday. Please try again later.";
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = "We couldn't submit this review to Workday. Please try again later.";
    } finally {
        stopLoading();
        inFlight.value = false;
    }
}
</script>
